import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Truck } from 'lucide-react'

import {
    posMaterialEffective,
    posOrderItemIndex,
    posOrderTotals,
} from './pos-order-payload'
import { PosLotSection } from './pos-lot-section'
import type { OrderTab } from './pos-types'
import type { CreateOrderRequest } from '@/lib/api/sales-order-api'
import type { ProvinceWardValue } from '@/components/forms/province-ward-picker'
import type { InvoiceBuyerFormValue } from '@/components/forms/invoice-buyer-fields'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { salesOrderApi } from '@/lib/api/sales-order-api'
import { ProvinceWardPicker } from '@/components/forms/province-ward-picker'
import {
    DEFAULT_CREATE_INVOICE_BUYER,
    InvoiceBuyerFields,
    applyBuyerPatch,
    buildInvoiceBuyerPayload,
} from '@/components/forms/invoice-buyer-fields'
import { ShippingCarrierSelect } from '@/components/forms/shipping-carrier-select'
import { CodFreightFields } from '@/components/orders/cod-freight-fields'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { usePosLotAllocation } from '@/hooks/use-pos-lot-allocation'
import { orderKeys } from '@/hooks/use-sales-order-query'
import { useAuth } from '@/hooks/use-auth'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { posthog } from '@/lib/posthog'
import { DEFAULT_SHIPPING_CARRIER_ID } from '@/lib/constants'

export interface PosDeliveryCheckoutHandle {
    submit: () => void
}

interface Props {
    workflow?: ReturnType<typeof useWorkflowTracking>
    tab: OrderTab
    onSuccess: () => void
}

/**
 * Inline checkout panel for "Bán giao hàng" (deliver-later) mode. Unlike the
 * quick-sale panel it has NO payment section — the order is created COD and
 * lands at stock_out so a driver can pick it up. Requires a real customer and
 * a delivery address.
 */
export const PosDeliveryCheckoutPanel = forwardRef<
    PosDeliveryCheckoutHandle,
    Props
>(function PosDeliveryCheckoutPanel(
    { tab, onSuccess, workflow: parentWorkflow },
    ref,
) {
    const localWorkflow = useWorkflowTracking('pos_delivery_checkout', {
        attemptKey: tab.id,
        active: !parentWorkflow,
        retainAttempts: true,
    })
    const workflow = parentWorkflow ?? localWorkflow
    const qc = useQueryClient()
    const { hasPermission } = useAuth()
    const canEditDiscount = hasPermission('sales_orders.edit_discount')
    const { data: invoiceSettings } = useInvoiceSettings()
    const vatPercent = invoiceSettings?.vat_percent ?? 0

    const [orderDiscountPercent, setOrderDiscountPercent] = useState(0)
    const [customerPhone, setCustomerPhone] = useState(
        tab.customer?.phone ?? '',
    )
    const [selectedCarrierId, setSelectedCarrierId] = useState(
        DEFAULT_SHIPPING_CARRIER_ID,
    )
    const [shippingAddress, setShippingAddress] = useState(
        tab.customer?.address ?? '',
    )
    const [shipping, setShipping] = useState<ProvinceWardValue>({
        provinceCode: tab.customer?.province_code ?? undefined,
        provinceName: tab.customer?.province ?? undefined,
        wardCode: tab.customer?.ward_code ?? undefined,
        wardName: tab.customer?.ward ?? undefined,
    })

    // Sync editable fields when the selected customer changes
    useEffect(() => {
        setCustomerPhone(tab.customer?.phone ?? '')
        setShippingAddress(tab.customer?.address ?? '')
        setShipping({
            provinceCode: tab.customer?.province_code ?? undefined,
            provinceName: tab.customer?.province ?? undefined,
            wardCode: tab.customer?.ward_code ?? undefined,
            wardName: tab.customer?.ward ?? undefined,
        })
    }, [tab.customer?.id, tab.customer?.phone, tab.customer?.address])

    // HĐĐT: đơn giao sau đi luồng giao hàng thật, nhưng cron nháp hóa đơn vẫn
    // chỉ nhặt đơn có tick issue_einvoice — POS phải gửi tick như form chuẩn.
    const einvoiceEnabled = useFeatureFlag('dev-einvoice')
    const [invoiceBuyer, setInvoiceBuyer] = useState<InvoiceBuyerFormValue>(
        DEFAULT_CREATE_INVOICE_BUYER,
    )

    // Mã đơn nội bộ + thu COD / bên trả cước + trừ số dư khách — cùng luật với
    // form bán hàng chuẩn. collect_cod mặc định "Có"; tick trừ số dư → tắt COD.
    const [internalCode, setInternalCode] = useState('')
    const [collectCod, setCollectCod] = useState<boolean | undefined>(true)
    const [feePayer, setFeePayer] = useState<
        'customer' | 'company' | undefined
    >(undefined)
    const customerCredit = Math.max(0, -(tab.customer?.current_debt ?? 0))
    const [applyCredit, setApplyCredit] = useState(false)
    const lots = usePosLotAllocation(tab, true)

    useEffect(() => {
        setSelectedCarrierId(DEFAULT_SHIPPING_CARRIER_ID)
        setInvoiceBuyer(DEFAULT_CREATE_INVOICE_BUYER)
        setInternalCode('')
        setCollectCod(true)
        setFeePayer(undefined)
        setApplyCredit(false)
    }, [tab.id])

    useEffect(() => {
        setApplyCredit(false)
        setCollectCod(true)
    }, [tab.customer?.id])

    // Dùng chung posOrderTotals với panel quick để mọi màn POS ra cùng một số
    // tiền (M-78: tổng theo giá gồm VAT từng đơn vị).
    const { priceOf: materialPriceOf } = posMaterialEffective(
        tab.items,
        tab.packagingItems,
    )
    const {
        itemSubtotal,
        materialTotal,
        baseAmount,
        subtotal,
        vatAmount,
        totalAmount,
    } = posOrderTotals(
        tab.items,
        tab.packagingItems,
        orderDiscountPercent,
        vatPercent,
    )
    const itemCount = tab.items.reduce((s, i) => s + i.quantity, 0)

    const hasCustomer = tab.customer !== null
    const phoneOk = customerPhone.trim().length >= 8
    const addressOk = shippingAddress.trim().length >= 5

    const createDelivery = useMutation({
        mutationFn: ({
            order,
            workflow: submittedWorkflow,
        }: {
            order: CreateOrderRequest
            workflow: ReturnType<typeof useWorkflowTracking>
        }) =>
            submittedWorkflow.run(
                async () => {
                    const res = await salesOrderApi.createDelivery(order)
                    if (!res.data)
                        throw new Error(m.pos_checkout_create_delivery_error())
                    return res.data
                },
                (result) => result.id,
            ),
        onSuccess: (data) => {
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(
                m.toast_pos_delivery_created({ code: data.order_code }),
            )
            posthog.capture('pos_delivery_order_created', {
                order_id: data.id,
                total_amount: totalAmount,
                item_count: tab.items.length,
            })
            setSelectedCarrierId(DEFAULT_SHIPPING_CARRIER_ID)
            setInvoiceBuyer(DEFAULT_CREATE_INVOICE_BUYER)
            setInternalCode('')
            setCollectCod(true)
            setFeePayer(undefined)
            setApplyCredit(false)
            onSuccess()
        },
        onError: () => {
            toast.error(m.toast_pos_order_error())
        },
    })

    const hasSellable = tab.items.length > 0 || materialTotal > 0
    const canSubmit =
        hasSellable &&
        hasCustomer &&
        phoneOk &&
        addressOk &&
        !createDelivery.isPending

    const handleSubmit = () => {
        if (!canSubmit || !tab.customer) {
            workflow.step('validation_failed')
            return
        }
        // Lô chưa phân bổ đủ: chặn nhưng phải NÓI RÕ, không im lặng — khối lô
        // có thể đang nằm ngoài tầm nhìn khi bấm nút ở cuối panel.
        if (lots.hasError) {
            workflow.step('validation_failed')
            toast.error(m.stock_out_submit_blocked())
            return
        }
        createDelivery.mutate({
            workflow,
            order: {
                source_channel: 'pos_delivery',
                customer_id: tab.customer.id,
                customer_name: tab.customer.name,
                customer_phone: customerPhone.trim(),
                shipping_address: shippingAddress.trim(),
                shipping_province: shipping.provinceName || undefined,
                shipping_province_code: shipping.provinceCode || undefined,
                shipping_ward: shipping.wardName || undefined,
                shipping_ward_code: shipping.wardCode || undefined,
                product_notes: tab.note || undefined,
                internal_order_code: internalCode.trim() || undefined,
                shipping_carrier_id:
                    selectedCarrierId || DEFAULT_SHIPPING_CARRIER_ID,
                // Tri-state như form chuẩn: undefined ("không chọn") gửi null.
                collect_cod: collectCod ?? null,
                shipping_fee_payer: feePayer,
                apply_credit: applyCredit && customerCredit > 0,
                issue_einvoice: einvoiceEnabled ? invoiceBuyer.enabled : false,
                invoice_buyer: einvoiceEnabled
                    ? buildInvoiceBuyerPayload(invoiceBuyer, 'create')
                    : undefined,
                vat_percent: vatPercent,
                vat_amount: vatAmount,
                discount_percent:
                    orderDiscountPercent > 0 ? orderDiscountPercent : undefined,
                items: tab.items.map((item) => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    discount_percent: item.discount_percent,
                    material_price_split:
                        item.material_price_split || undefined,
                })),
                packaging_items: (tab.packagingItems ?? [])
                    .filter((p) => p.quantity > 0)
                    .map((p) => ({
                        packaging_material_id: p.packaging_material_id,
                        quantity: p.quantity,
                        source: p.source,
                        // Dòng GỘP gửi giá 0 (giá vật tư trong giá SP).
                        unit_price: materialPriceOf(p),
                        discount_percent: p.discount_percent,
                        product_id: p.product_id || undefined,
                        order_item_index: posOrderItemIndex(
                            tab.items,
                            p.line_key,
                        ),
                    })),
                ...lots.buildPayload(),
            },
        })
    }

    const handleSubmitRef = useRef(handleSubmit)
    handleSubmitRef.current = handleSubmit
    useImperativeHandle(
        ref,
        () => ({ submit: () => handleSubmitRef.current() }),
        [],
    )

    return (
        <div
            onFocusCapture={() => workflow.interact('details')}
            className="flex-1 flex flex-col min-h-0 overflow-y-auto"
        >
            <div className="flex-1 p-3 space-y-3 text-sm">
                {/* Order summary */}
                <div className="space-y-1.5">
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">
                            {m.order_items_subtotal()}
                            <span className="ml-2 text-xs">({itemCount})</span>
                        </span>
                        <span className="font-medium">
                            {formatVND(itemSubtotal)}
                        </span>
                    </div>
                    {canEditDiscount && (
                        <div className="flex items-center justify-between gap-2">
                            <label className="text-muted-foreground shrink-0">
                                {m.order_discount_percent()}
                            </label>
                            <div className="flex items-center gap-1">
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={0.5}
                                    value={orderDiscountPercent}
                                    onChange={(e) =>
                                        setOrderDiscountPercent(
                                            Math.min(
                                                100,
                                                Math.max(
                                                    0,
                                                    Number(e.target.value),
                                                ),
                                            ),
                                        )
                                    }
                                    className="w-14 text-center border-b border-dashed border-primary/50 bg-transparent text-sm focus:outline-none focus:border-primary"
                                />
                                <span className="text-xs text-muted-foreground">
                                    %
                                </span>
                            </div>
                        </div>
                    )}
                    {orderDiscountPercent > 0 && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                                {m.order_discount_label({
                                    percent: String(orderDiscountPercent),
                                })}
                            </span>
                            <span className="text-destructive">
                                -{formatVND(baseAmount - subtotal)}
                            </span>
                        </div>
                    )}
                    {materialTotal > 0 && (
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">
                                {m.order_material_total()}
                            </span>
                            <span>{formatVND(materialTotal)}</span>
                        </div>
                    )}
                    {vatPercent > 0 && (
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">
                                {m.order_vat_label({
                                    percent: String(vatPercent),
                                })}
                            </span>
                            <span>{formatVND(vatAmount)}</span>
                        </div>
                    )}
                </div>

                {/* Total */}
                <div className="flex justify-between items-center pt-1 border-t">
                    <span className="font-semibold">
                        {m.customer_need_to_pay()}
                    </span>
                    <span className="text-primary text-lg font-bold">
                        {formatVND(totalAmount)}
                    </span>
                </div>

                {!hasCustomer && (
                    <p className="text-xs text-destructive">
                        {m.pos_delivery_need_customer()}
                    </p>
                )}

                {/* Customer phone */}
                <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                        {m.customer_phone_label()} *
                    </label>
                    <input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        minLength={8}
                        required
                        disabled={!hasCustomer}
                        className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm disabled:opacity-50"
                    />
                </div>

                {/* Shipping carrier */}
                <div>
                    <label
                        htmlFor="pos-delivery-shipping-carrier"
                        className="text-xs text-muted-foreground mb-1 block"
                    >
                        {m.order_form_carrier_label()}
                    </label>
                    <ShippingCarrierSelect
                        id="pos-delivery-shipping-carrier"
                        value={selectedCarrierId}
                        onValueChange={(value) => {
                            workflow.step('selection_changed')
                            setSelectedCarrierId(value)
                        }}
                    />
                </div>

                {einvoiceEnabled && (
                    <InvoiceBuyerFields
                        idPrefix="pos-delivery"
                        value={invoiceBuyer}
                        onChange={(patch) =>
                            setInvoiceBuyer((prev) =>
                                applyBuyerPatch(prev, patch, tab.customer),
                            )
                        }
                    />
                )}

                <div>
                    <label
                        htmlFor="pos-delivery-internal-code"
                        className="text-xs text-muted-foreground mb-1 block"
                    >
                        {m.order_internal_code_label()}
                    </label>
                    <input
                        id="pos-delivery-internal-code"
                        value={internalCode}
                        maxLength={64}
                        onChange={(e) => setInternalCode(e.target.value)}
                        placeholder={m.order_internal_code_placeholder()}
                        className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
                    />
                </div>

                {customerCredit > 0 && (
                    <label className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 p-2 text-sm">
                        <input
                            type="checkbox"
                            checked={applyCredit}
                            onChange={(e) => {
                                const v = e.target.checked
                                setApplyCredit(v)
                                // Bật → tắt thu COD (cấn số dư); tắt → về "Có".
                                setCollectCod(!v)
                            }}
                            className="size-4 accent-primary"
                        />
                        <span className="font-medium text-green-800">
                            {m.order_credit_apply_label()} (
                            {formatVND(customerCredit)})
                        </span>
                    </label>
                )}

                {/* Thu tiền (COD) + Thu cước — dùng chung component với form chuẩn. */}
                <CodFreightFields
                    idPrefix="pos-delivery"
                    codDisabled={applyCredit}
                    collectCod={collectCod}
                    onCollectCodChange={setCollectCod}
                    feePayer={feePayer}
                    onFeePayerChange={setFeePayer}
                />

                <PosLotSection lots={lots} />

                {/* Delivery address */}
                <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                        {m.pos_delivery_address_label()} *
                    </label>
                    <textarea
                        value={shippingAddress}
                        onChange={(e) => setShippingAddress(e.target.value)}
                        rows={2}
                        required
                        disabled={!hasCustomer}
                        className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm disabled:opacity-50"
                    />
                </div>

                {/* Structured province/ward (optional) */}
                <ProvinceWardPicker
                    value={shipping}
                    onChange={setShipping}
                    disabled={!hasCustomer}
                />
            </div>

            {/* Submit button — pinned to bottom */}
            <div className="p-2 border-t shrink-0">
                {!hasSellable ? (
                    <div className="w-full rounded bg-muted text-muted-foreground text-center py-3 text-sm">
                        {m.pos_add_products_to_order()}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground font-bold py-4 text-base transition-colors"
                    >
                        <Truck className="size-5" />
                        {createDelivery.isPending
                            ? m.common_processing()
                            : m.pos_create_delivery_order()}
                    </button>
                )}
            </div>
        </div>
    )
})
